import React, { useState, useEffect, useRef, useCallback } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

const scoreTag = (v) => v >= 75 ? { fg: '#16a34a', bg: 'rgba(22,163,74,0.12)' }
  : v >= 50 ? { fg: '#d97706', bg: 'rgba(217,119,6,0.12)' }
  : { fg: 'var(--brand)', bg: 'rgba(226,35,26,0.10)' };

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'à l\'instant';
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  return `il y a ${Math.round(s / 86400)} j`;
};

const ETAPES = { rerank: 'Présélection du vivier', scoring: 'Scoring des profils' };

/**
 * « Scan du vivier » : cherche les top N profils de toute la base pour un besoin.
 * POST /besoins/:id/scan-vivier (202 ; 409 si déjà en cours) puis poll GET ~3 s.
 * Le dernier scan reste consultable (une ligne par besoin).
 */
export default function ScanVivier({ besoin, onClose, onOpenCandidat, onPositioned, showNotification, theme = 'light' }) {
  const [topN, setTopN] = useState('10');       // '3' | '5' | '10' | 'custom'
  const [customN, setCustomN] = useState('15');
  const [localisation, setLocalisation] = useState('');
  const [dispo, setDispo] = useState('');
  const [excludePos, setExcludePos] = useState(true);
  const [scan, setScan] = useState(null);       // dernier état GET /besoins/:id/scan-vivier
  const [loading, setLoading] = useState(true); // premier GET
  const [launching, setLaunching] = useState(false);
  const [positioning, setPositioning] = useState(null); // candidate_id en cours de positionnement
  const [positioned, setPositioned] = useState(() => new Set()); // positionnés depuis ce panneau
  const pollRef = useRef(null);

  const fetchScan = useCallback(async () => {
    const r = await fetch(`${BASE}/besoins/${besoin.id}/scan-vivier`, { headers: authH() });
    if (r.status === 404) return null; // aucun scan pour ce besoin
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const j = await r.json().catch(() => ({}));
    return j.data || j;
  }, [besoin.id]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const d = await fetchScan();
        setScan(d);
        if (!d || d.status !== 'running') clearInterval(pollRef.current);
      } catch { /* on retente au prochain tick */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, [fetchScan]);

  // Dernier scan au montage ; s'il tourne encore, on reprend le suivi.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchScan();
        if (cancelled) return;
        setScan(d);
        if (d?.status === 'running') startPolling();
      } catch { /* pas de scan */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [fetchScan, startPolling]);

  const launch = async () => {
    const n = topN === 'custom' ? Math.max(1, Math.min(25, parseInt(customN, 10) || 10)) : parseInt(topN, 10);
    setLaunching(true);
    try {
      const body = { top_n: n, exclude_positionnes: excludePos };
      if (localisation.trim()) body.localisation = localisation.trim();
      if (dispo.trim()) body.disponibilite = dispo.trim();
      const r = await fetch(`${BASE}/besoins/${besoin.id}/scan-vivier`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { showNotification?.('Un scan tourne déjà pour ce besoin — suivi repris.', 'error'); startPolling(); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setPositioned(new Set());
      startPolling();
    } catch (e) { showNotification?.(`Échec du lancement : ${e.message}`, 'error'); }
    finally { setLaunching(false); }
  };

  // Positionner via l'endpoint existant, avec l'id CANONIQUE.
  const positionner = async (p) => {
    const cid = p.candidate_id;
    if (cid == null) return;
    setPositioning(cid);
    try {
      const r = await fetch(`${BASE}/candidates/${cid}/besoins`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: besoin.id, besoinId: besoin.id }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      setPositioned(prev => new Set(prev).add(cid));
      showNotification?.(`${p.nom || 'Candidat'} positionné sur le besoin`, 'success');
      onPositioned?.();
    } catch (e) { showNotification?.('Erreur positionnement : ' + e.message, 'error'); }
    finally { setPositioning(null); }
  };

  const running = scan?.status === 'running';
  const done = scan?.status === 'done';
  const error = scan?.status === 'error';
  const top = done && Array.isArray(scan?.results?.top) ? scan.results.top : [];
  const meta = scan?.results?.meta || {};
  const prog = scan?.progress || {};
  const progPct = prog.total ? Math.min(100, Math.round(((prog.scored ?? 0) / prog.total) * 100)) : (running ? 10 : 0);

  const card = { background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' };
  const miniBtn = { fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, padding: '5px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', cursor: 'pointer' };
  const inputSt = { padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', outline: 'none' };
  const lbl = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '5px', display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,11,0.6)', backdropFilter: 'blur(2px)', zIndex: 600, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div className="ats-scope" data-theme={theme} style={{ width: 'min(880px, 96vw)', background: 'var(--bg, #0b0c11)', height: '100%', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <BI icon="intersect" style={{ color: 'var(--brand)', fontSize: '17px' }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Scan du vivier</div>
            <div style={{ fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{besoin.titre}{besoin.client ? ` · ${besoin.client}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: '17px' }}><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Réglages */}
          <div style={card}>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <span style={lbl}>Top N</span>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {['3', '5', '10'].map(v => (
                    <button key={v} onClick={() => setTopN(v)} style={{ ...miniBtn, background: topN === v ? 'var(--accent)' : 'var(--ghost-bg)', color: topN === v ? 'var(--accent-fg)' : 'var(--text-2)', border: topN === v ? 'none' : miniBtn.border }}>{v}</button>
                  ))}
                  <button onClick={() => setTopN('custom')} style={{ ...miniBtn, background: topN === 'custom' ? 'var(--accent)' : 'var(--ghost-bg)', color: topN === 'custom' ? 'var(--accent-fg)' : 'var(--text-2)', border: topN === 'custom' ? 'none' : miniBtn.border }}>Autre</button>
                  {topN === 'custom' && <input value={customN} onChange={e => setCustomN(e.target.value.replace(/\D/g, ''))} style={{ ...inputSt, width: '52px', textAlign: 'center' }} title="1 à 25" />}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <span style={lbl}>Localisation (optionnel)</span>
                <input value={localisation} onChange={e => setLocalisation(e.target.value)} placeholder="ex. Genève" style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <span style={lbl}>Disponibilité (optionnel)</span>
                <input value={dispo} onChange={e => setDispo(e.target.value)} placeholder="ex. immédiate" style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={excludePos} onChange={e => setExcludePos(e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                Exclure les candidats déjà positionnés
              </label>
              <button className="ats-btn ats-btn-primary" onClick={launch} disabled={launching || running}
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, opacity: (launching || running) ? 0.6 : 1 }}>
                <BI icon={launching || running ? 'arrow-repeat' : 'stars'} style={(launching || running) ? { animation: 'ats-spin .8s linear infinite' } : undefined} />
                {running ? 'Scan en cours…' : done || error ? 'Relancer' : 'Trouver les meilleurs profils'}
              </button>
            </div>
            {done && scan?.started_at && (
              <div style={{ marginTop: '9px', fontSize: '11.5px', color: 'var(--text-faint)' }}>
                Dernier scan calculé {timeAgo(scan.started_at)}{meta.shortlist != null ? ` · ${meta.shortlist} présélectionnés` : ''}{meta.scored != null ? ` · ${meta.scored} scorés` : ''}{meta.duration ? ` · ${Math.round(meta.duration)} s` : ''}
              </div>
            )}
          </div>

          {/* Progression */}
          {running && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '7px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-2)' }}>
                  <BI icon="arrow-repeat" style={{ animation: 'ats-spin .8s linear infinite' }} /> {ETAPES[prog.etape] || 'Analyse du vivier'}…
                </span>
                <span className="ats-mono" style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                  {prog.etape === 'scoring' && prog.total ? `${prog.scored ?? 0}/${prog.total}` : (prog.shortlist != null ? `${prog.shortlist} présélectionnés` : '')}
                </span>
              </div>
              <div style={{ height: '7px', borderRadius: '99px', background: 'var(--chip-bg)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progPct}%`, background: 'var(--brand)', borderRadius: '99px', transition: 'width .4s ease' }} />
              </div>
            </div>
          )}

          {error && (
            <div style={{ ...card, border: '1px solid rgba(226,35,26,0.30)', background: 'rgba(226,35,26,0.06)', fontSize: '12.5px', color: 'var(--text-2)' }}>
              <BI icon="exclamation-triangle" style={{ color: 'var(--brand)' }} /> Le scan a échoué{scan?.error ? ` : ${scan.error}` : ''}. Relance-le.
            </div>
          )}

          {/* Résultats */}
          {loading ? (
            <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>Chargement…</div>
          ) : done && top.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>Aucun profil trouvé avec ces critères.</div>
          ) : top.map((p, i) => {
            const cid = p.candidate_id;
            const isPos = p.deja_positionne || (cid != null && positioned.has(cid));
            const nonEval = p.score == null;
            const v = nonEval ? null : Math.round(Number(p.score));
            const tag = nonEval ? null : scoreTag(v);
            const sm = p.skills_match || [];
            const mm = p.skills_missing || [];
            return (
              <div key={cid ?? i} style={{ ...card, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="ats-mono" style={{ flex: 'none', width: '24px', fontSize: '12px', color: 'var(--text-faint)' }}>#{i + 1}</span>
                  <div style={{ width: '38px', height: '38px', borderRadius: '9px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", background: 'var(--chip-bg)', color: 'var(--text-strong)' }}>
                    {(p.nom || '··').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span onClick={() => cid != null && onOpenCandidat?.(cid)} style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)', cursor: cid != null ? 'pointer' : 'default' }}>{p.nom || `Candidat ${cid || ''}`}</span>
                      {isPos && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'rgba(22,163,74,0.12)', color: '#16a34a' }}><BI icon="check-circle" /> déjà sur ce besoin</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[p.titre, p.ville, p.disponibilite].filter(Boolean).join(' · ') || '—'}</div>
                    {p.resume_1_ligne && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>{p.resume_1_ligne}</div>}
                  </div>
                  <div style={{ flex: 'none', textAlign: 'right' }}>
                    {nonEval
                      ? <span title={p.motif_non_evaluable || 'Non évaluable'} style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}>Non évaluable</span>
                      : <span className="ats-mono" title={p.verdict ? `Verdict : ${p.verdict}` : undefined} style={{ fontSize: '17px', fontWeight: 700, color: tag.fg, background: tag.bg, padding: '4px 10px', borderRadius: '8px' }}>{v}</span>}
                  </div>
                  <button style={{ ...miniBtn, flex: 'none', border: 'none', background: isPos ? 'var(--chip-bg)' : 'var(--accent)', color: isPos ? 'var(--text-3)' : 'var(--accent-fg)', cursor: isPos ? 'default' : 'pointer' }}
                    disabled={isPos || positioning === cid} onClick={() => positionner(p)}>
                    <BI icon={positioning === cid ? 'arrow-repeat' : 'plus-lg'} style={positioning === cid ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {isPos ? 'Positionné' : 'Positionner'}
                  </button>
                </div>
                {(sm.length > 0 || mm.length > 0 || p.skill_coverage || p.rerank_raison) && (
                  <div style={{ marginTop: '9px', paddingLeft: '74px' }}>
                    {(sm.length > 0 || mm.length > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        {p.skill_coverage && <span style={{ fontSize: '11px', color: 'var(--text-3)', marginRight: '4px' }}>{p.skill_coverage}</span>}
                        {sm.slice(0, 6).map((s, k) => <span key={`s${k}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 500, padding: '2px 7px', borderRadius: '5px', background: 'rgba(22,163,74,0.12)', color: '#16a34a' }}><BI icon="check-lg" /> {s}</span>)}
                        {mm.slice(0, 4).map((s, k) => <span key={`m${k}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: 500, padding: '2px 7px', borderRadius: '5px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}><BI icon="dash-lg" /> {s}</span>)}
                      </div>
                    )}
                    {p.rerank_raison && <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontStyle: 'italic', marginTop: '4px' }}>{p.rerank_raison}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
